/**
 * Alert Email Dispatcher — Phase 17.4 (2026-04-28) + Phase 17 Tier-2 §2.1 (2026-04-30)
 * + P22.2.1 (2026-04-30) extended quality_dip + cost_overrun coverage
 * + Phase 23.5 (2026-05-01) extended idle-annotator coverage
 * + Phase 23.6 (2026-05-01) extended batch_stuck + revision_spiral coverage.
 *
 * Wires SPL-actionable alert types from alertService.js to email_event_type
 * emails. Phase 17.4 covered 6 types (overtime, schedule, budget, escalation,
 * projection_deadline, projection_budget). P22.2.1 added 2 more after Phase
 * 22.2 dogfood revealed they were in-app-only despite being highly actionable:
 *   - quality          → spl_alert_quality_dip      (annotator below threshold
 *                                                    or declining trend → retrain)
 *   - cost_overrun     → spl_alert_cost_overrun     (spend-now outpaces delivery-
 *                                                    now by ≥20pp — efficiency
 *                                                    gap leading indicator)
 * Phase 23.5 added the upstream signal for the batch-reassign recovery flow:
 *   - idle             → spl_alert_idle_annotator   (last_active_at > 3 days)
 * Phase 23.6 completed the actionable-alert coverage:
 *   - batch_stuck      → spl_alert_batch_stuck      (in-progress batch taking
 *                                                    >1.5× project avg → check-in
 *                                                    or reassign)
 *   - revision_spiral  → spl_alert_revision_spiral  (revision_count ≥ max →
 *                                                    quality issue persists →
 *                                                    senior reassign or
 *                                                    calibration session)
 *
 * Still in-app only (per-day rollups, not real-time-actionable):
 *   activity
 *
 * Severity tiering (per PagerDuty 4-tier + scope §2 17.4):
 *   - critical → real-time email (now)
 *   - warning  → real-time email (with quiet-hours suppression for non-critical)
 *   - info     → in-app only (no email)
 *
 * Quiet hours (Phase 17 Tier-2 §2.1 fix — was using browser local time):
 *   - 10pm – 7am LOCAL TO RECIPIENT (per-recipient via individual.timezone): non-critical
 *     alerts suppressed until next morning. Critical alerts fire anytime.
 *   - Phase 17 base shipped buggy `now.getHours()` which used dispatcher's browser
 *     local time, not recipient's timezone. Tier-2 audit (Agent B) caught it. Fixed
 *     2026-04-30 via src/utils/timezone.js + recipientTimezone parameter.
 *
 * Idempotency:
 *   - key = `${event_type}:${entityId}:${recipientId}:${dateBucket}`
 *   - dateBucket size matches alert cadence:
 *     - 24h cadence → YYYY-MM-DD (one per day per entity per recipient)
 *     - 12h cadence → YYYY-MM-DD-AM/PM
 *     -  no dedup    → YYYY-MM-DDTHH:MM (per-minute, effectively no dedup)
 *   - Combined with edge-function idempotency check (Phase 17.7.3) prevents
 *     same alert email from firing twice in the bucket window.
 *
 * Demo-org email suppression (Phase 17 Tier-2 §2.1 — Agent B finding):
 *   - Recipients with email matching `*@example.com` are skipped.
 *   - Demo org seeded with @example.com individuals (migration 287); without
 *     suppression, opening the dashboard in dev with a Demo SPL session would
 *     fire real Resend emails to fake addresses.
 *
 * Adaptive suppression + true daily-digest cron deferred to a follow-up
 * (Phase 17 Tier-2 §2.5 — gated on post-DMARC + customer-1 + 30d data signal).
 */

const ALERT_EMAIL_MAP = {
  // alertType → { event_type, severity, dedupWindowHours }
  // null severity means "derive from alert.severity at dispatch time"
  overtime: { event: 'spl_overtime_warning', severity: 'warning', dedupWindowHours: 24 },
  schedule: { event: 'spl_schedule_alert', severity: null, dedupWindowHours: 24 },
  budget: { event: 'spl_budget_alert', severity: null, dedupWindowHours: 12 },
  escalation: { event: 'spl_escalation_alert', severity: 'critical', dedupWindowHours: 0 },
  projection_deadline: { event: 'spl_projection_deadline', severity: 'warning', dedupWindowHours: 24 },
  projection_budget: { event: 'spl_projection_budget', severity: 'warning', dedupWindowHours: 24 },
  // P22.2.1 (Chapter 3 dogfood close 2026-04-30): added after Phase 22.2
  // dogfood revealed quality alerts (Maya scored Zara 75% → dashboard fired
  // critical alert badge but NO email). 24h dedup so re-renders during the
  // same workday don't spam (same dismissKey × same SPL × same date bucket).
  quality: { event: 'spl_alert_quality_dip', severity: null, dedupWindowHours: 24 },
  // P22.2.1: cost-vs-progress efficiency gap. Distinct from `budget` (which
  // is utilization-based). 12h dedup matches budget's cadence (efficiency
  // can flip mid-day if a batch lands).
  cost_overrun: { event: 'spl_alert_cost_overrun', severity: null, dedupWindowHours: 12 },
  // Phase 23.5 (adversarial use case discovery 2026-05-01): the upstream
  // signal for the Phase 23.1 reassign-batch flow. computeIdleAlerts
  // detects `individual.last_active_at > 3 days` (warning) or `> 6 days`
  // (critical). 24h dedup so the SPL doesn't get re-emailed every render
  // for the same idle annotator on the same day.
  idle: { event: 'spl_alert_idle_annotator', severity: null, dedupWindowHours: 24 },
  // Phase 23.6 (adversarial use case discovery 2026-05-01): completes
  // actionable-alert email coverage. computeBatchStuckAlerts detects an
  // In Progress batch taking longer than 1.5× project average (warning)
  // or 2.5× average (critical). 24h dedup matches idle/quality cadence.
  batch_stuck: { event: 'spl_alert_batch_stuck', severity: null, dedupWindowHours: 24 },
  // Phase 23.6: computeRevisionSpiralAlerts detects revision_count ≥
  // maxRevisions (warning) or ≥ maxRevisions+2 (critical). The pattern
  // resolves slowly (one rejection cycle = days), so 24h dedup is
  // sufficient — the email persists in the SPL's inbox until the next
  // bucket regardless of dashboard re-renders.
  revision_spiral: { event: 'spl_alert_revision_spiral', severity: null, dedupWindowHours: 24 },
};

// Phase 17 Tier-2 §2.1 — per-recipient timezone arithmetic
import { isQuietHoursForRecipient, DEFAULT_TIMEZONE } from '../utils/timezone';
// P22.2.1 carry-forward (2026-05-01) — centralized reserved-domain check
// shared with emailService so EVERY email-firing path benefits, not just
// dashboard alerts.
import { isReservedEmail } from '../utils/emailReserved';

// Session-scoped fired-key cache. Prevents re-firing the same alert
// across re-renders within a single browser session. Edge-function
// idempotency check provides cross-session dedup via email_log.idempotency_key.
const firedThisSession = new Set();

/**
 * Determine if "now" is in the recipient's quiet hours (10pm-7am LOCAL to recipient).
 * Phase 17 Tier-2 §2.1 fix — was using browser's local hour (`now.getHours()`)
 * which is the dispatcher's browser timezone, not the recipient's. For an SPL in
 * PST receiving alerts dispatched from a server in UTC, the previous code would
 * fire alerts during their actual quiet hours.
 *
 * @param {Date} now - current wall-clock time
 * @param {string} recipientTimezone - IANA timezone of the recipient (from
 *   individual.timezone column; defaults to 'UTC' if missing)
 * @returns {boolean}
 */
function isQuietHours(now, recipientTimezone = DEFAULT_TIMEZONE) {
  return isQuietHoursForRecipient(recipientTimezone, now);
}

// Suppress emails to demo / test addresses — delegated to shared helper
// (P22.2.1 carry-forward). See src/utils/emailReserved.js for coverage rules.
const isSuppressedRecipient = isReservedEmail;

function bucketForWindow(windowHours, now) {
  const yyyymmdd = now.toISOString().slice(0, 10);
  if (windowHours >= 24) return yyyymmdd;
  if (windowHours >= 12) return `${yyyymmdd}-${now.getHours() < 12 ? 'AM' : 'PM'}`;
  // 0 hours = no dedup → use minute-level bucket so each fresh dispatch
  // gets a unique key and the edge function won't dedup it.
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  return `${yyyymmdd}T${hh}:${mm}`;
}

/**
 * Dispatch emails for a list of in-app alerts.
 *
 * @param {Array} alerts - alert objects from alertService.computeAllAlerts
 * @param {Object} ctx
 * @param {string} ctx.orgId - org context for trigger lookup + email_log
 * @param {string} ctx.splUserId - recipient SPL's user id (resolves to email)
 * @param {string} [ctx.splEmail] - SPL's email if pre-resolved (avoids DB hit)
 * @param {string} [ctx.splTimezone] - SPL's IANA timezone (from individual.timezone;
 *   Phase 17 Tier-2 §2.1 — was missing; quiet-hours used browser tz). Defaults to UTC.
 * @param {Date}   [ctx.now] - injected wall-clock for testability
 * @returns {Promise<{dispatched: number, skipped: number}>}
 */
export async function dispatchAlertEmails(alerts, { orgId, splUserId, splEmail, splTimezone = DEFAULT_TIMEZONE, now = new Date() } = {}) {
  if (!orgId || !splUserId || !Array.isArray(alerts) || alerts.length === 0) {
    return { dispatched: 0, skipped: 0 };
  }

  // Phase 17 Tier-2 §2.1 — Demo org email suppression
  if (splEmail && isSuppressedRecipient(splEmail)) {
    return { dispatched: 0, skipped: alerts.length };
  }

  // Phase 17 Tier-2 §2.1 — quiet-hours uses recipient's timezone, not dispatcher's
  const inQuiet = isQuietHours(now, splTimezone);
  let dispatched = 0;
  let skipped = 0;

  // Lazy import to avoid circular dependency (alertEmailDispatcher imports
  // emailService → emailService imports utils → potentially back here).
  const { fireAndSendEmail } = await import('./emailService');

  for (const alert of alerts) {
    const mapping = ALERT_EMAIL_MAP[alert?.type];
    if (!mapping) { skipped++; continue; }

    // Severity: derive from alert if mapping says null, else use mapping value.
    const severity = mapping.severity || alert.severity || 'warning';

    // Quiet hours: suppress non-critical alerts. Critical fires anytime.
    if (inQuiet && severity !== 'critical') { skipped++; continue; }

    const entityId = alert.entityId || alert.dismissKey || alert.jobId || 'unknown';
    const bucket = bucketForWindow(mapping.dedupWindowHours, now);
    const idempotencyKey = `${mapping.event}:${entityId}:${splUserId}:${bucket}`;

    // Session-scope dedup (cheap defense before hitting the edge function)
    if (firedThisSession.has(idempotencyKey)) { skipped++; continue; }

    try {
      const result = await fireAndSendEmail(orgId, mapping.event, {
        recipientId: splUserId,
        senderId: splUserId,
        ...(splEmail ? { email: splEmail } : {}),
        jobId: alert.jobId || null,
        alertType: alert.type,
        alertTitle: alert.title || '',
        alertMessage: alert.message || '',
        severity,
        actionUrl: alert.actionUrl || '',
      }, {
        recipientId: splUserId,
        jobId: alert.jobId || null,
        idempotencyKey,
      });
      if (result?.sent || result?.skipped) {
        firedThisSession.add(idempotencyKey);
        dispatched++;
      } else {
        skipped++;
      }
    } catch (err) {
      console.warn(`alertEmailDispatcher: ${mapping.event} fire failed:`, err?.message || err);
      skipped++;
    }
  }

  return { dispatched, skipped };
}

/**
 * Test helper — clear the session-scope fired-key cache.
 * NOT exposed in production paths; for unit tests only.
 */
export function _resetFiredCacheForTest() {
  firedThisSession.clear();
}
